import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

const FIELDS = [
  ["name", "Client Name", "text"],
  ["registered_address", "Registered Address", "text"],
  ["cin", "CIN", "mono"],
  ["pan", "PAN", "mono"],
  ["gstin", "GSTIN", "mono"],
  ["tan", "TAN", "mono"],
  ["udhyam", "Udhyam Number", "mono"],
  ["date_of_incorporation", "Date of Incorporation", "date"],
  ["contact_email", "Contact Email", "text"],
  ["contact_phone", "Contact Phone", "text"],
];

export default function EditClientDialog({ open, onOpenChange, client, onSaved }) {
  const [editForm, setEditForm] = useState(client || {});
  const [customKey, setCustomKey] = useState("");
  const [customVal, setCustomVal] = useState("");

  // Reset editForm whenever the dialog opens with a (possibly different) client
  const handleOpenChange = (o) => {
    if (o) setEditForm(client || {});
    onOpenChange(o);
  };

  const addCustomField = () => {
    if (!customKey) return;
    setEditForm({
      ...editForm,
      custom_fields: { ...(editForm.custom_fields || {}), [customKey]: customVal },
    });
    setCustomKey(""); setCustomVal("");
  };

  const removeCustom = (k) => {
    const cf = { ...(editForm.custom_fields || {}) };
    delete cf[k];
    setEditForm({ ...editForm, custom_fields: cf });
  };

  const save = async () => {
    try {
      await api.patch(`/clients/${client.id}`, editForm);
      toast.success("Saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(parseApiError(e, "Save failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm" data-testid="dialog-edit-client">
        <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Edit Client</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
          {FIELDS.map(([k, label, type]) => (
            <div key={k} className={k === "registered_address" ? "col-span-2" : ""}>
              <Label className="text-xs">{label}</Label>
              {k === "registered_address" ? (
                <Textarea className="mt-1 rounded-sm" value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} />
              ) : (
                <Input className={`mt-1 rounded-sm ${type === "mono" ? "font-mono-data" : ""}`} type={type === "date" ? "date" : "text"} value={editForm[k] || ""} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} />
              )}
            </div>
          ))}
          {/* Custom fields */}
          <div className="col-span-2 border-t border-slate-200 pt-3">
            <div className="gc-label mb-2">Other Registrations / Custom Fields</div>
            {Object.entries(editForm.custom_fields || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 mb-1.5">
                <div className="w-1/3 text-xs font-medium">{k}</div>
                <Input
                  className="flex-1 rounded-sm font-mono-data"
                  value={v}
                  onChange={(e) => setEditForm({ ...editForm, custom_fields: { ...editForm.custom_fields, [k]: e.target.value } })}
                />
                <Button size="sm" variant="ghost" onClick={() => removeCustom(k)}>
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <Input placeholder="Field name (e.g., IEC Code)" value={customKey} onChange={(e) => setCustomKey(e.target.value)} className="rounded-sm" data-testid="input-custom-key" />
              <Input placeholder="Value" value={customVal} onChange={(e) => setCustomVal(e.target.value)} className="rounded-sm font-mono-data" data-testid="input-custom-value" />
              <Button variant="outline" className="rounded-sm" onClick={addCustomField} data-testid="btn-add-custom">Add</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} data-testid="btn-save-client">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
